import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Animated Network Canvas ──────────────────────────────────────────────────

function NetworkCanvas() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodesRef = useRef([]);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const NODE_COUNT = 38;
    const CONNECTION_DIST = 180;
    const COLORS = ['#2563EB', '#00D4CB', '#3b82f6', '#06b6d4', '#1d4ed8'];

    // Initialize nodes
    nodesRef.current = Array.from({ length: NODE_COUNT }, (_, i) => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 3 + 1.5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: 0.02 + Math.random() * 0.02,
    }));

    // Initialize particles along edges
    particlesRef.current = Array.from({ length: 25 }, () => ({
      fromIdx: Math.floor(Math.random() * NODE_COUNT),
      toIdx: Math.floor(Math.random() * NODE_COUNT),
      progress: Math.random(),
      speed: 0.003 + Math.random() * 0.004,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      size: Math.random() * 2 + 1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const nodes = nodesRef.current;
      const particles = particlesRef.current;
      const W = canvas.width;
      const H = canvas.height;

      // Move nodes
      nodes.forEach(n => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
        n.pulse += n.pulseSpeed;
      });

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.25;
            const grad = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            grad.addColorStop(0, `rgba(37, 99, 235, ${alpha})`);
            grad.addColorStop(1, `rgba(0, 212, 203, ${alpha})`);
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      nodes.forEach(n => {
        const pulseR = n.r + Math.sin(n.pulse) * 0.8;
        // Outer glow
        const glow = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, pulseR * 4);
        glow.addColorStop(0, n.color + '40');
        glow.addColorStop(1, n.color + '00');
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulseR * 4, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();
        // Core dot
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulseR, 0, Math.PI * 2);
        ctx.fillStyle = n.color + 'cc';
        ctx.fill();
      });

      // Move & draw particles
      particles.forEach(p => {
        p.progress += p.speed;
        if (p.progress >= 1) {
          p.progress = 0;
          p.fromIdx = p.toIdx;
          p.toIdx = Math.floor(Math.random() * nodes.length);
        }
        const from = nodes[p.fromIdx];
        const to = nodes[p.toIdx];
        if (!from || !to) return;
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > CONNECTION_DIST * 1.5) {
          p.progress = 0;
          p.fromIdx = Math.floor(Math.random() * nodes.length);
          return;
        }
        const px = from.x + dx * p.progress;
        const py = from.y + dy * p.progress;
        const alpha = Math.sin(p.progress * Math.PI) * 0.9;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.round(alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.65 }}
    />
  );
}

// ─── NexoSynX SVG Logo ──────────────────────────────────────────────────────────

function NexoSynXLogo({ size = 56, showText = true, light = true }) {
  return (
    <div className="flex items-center gap-3">
      {/* N icon */}
      <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="ngrad" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#00D4CB" />
          </linearGradient>
        </defs>
        {/* N body */}
        <path d="M10 44 L10 14 L18 14 L36 36 L36 14 L44 14 L44 44 L36 44 L18 22 L18 44 Z"
          fill="url(#ngrad)" />
        {/* Person circle on top of N */}
        <circle cx="36" cy="10" r="5" fill="#00D4CB" />
        {/* Chat bubble dot inside O of NexoSynX — small accent */}
        <circle cx="28" cy="44" r="2.5" fill="#00D4CB" opacity="0.7" />
      </svg>
      {showText && (
        <div className="flex flex-col">
          <span
            className="leading-none tracking-tight"
            style={{
              fontSize: size * 0.6,
              color: light ? '#FFFFFF' : '#0B1220',
              fontFamily: "'Oxygen', sans-serif",
              letterSpacing: '-0.02em',
            }}
          >
            <span style={{ fontWeight: 400 }}>Nexo</span>
            <span style={{ fontWeight: 700 }}>SynX</span>
          </span>
          <span
            className="font-semibold tracking-widest uppercase"
            style={{
              fontSize: size * 0.18,
              color: '#00D4CB',
              letterSpacing: '0.15em',
            }}
          >
            CRM Completo
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Login Component ──────────────────────────────────────────────────────────

export default function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 100);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      console.log('🚀 LOGIN TENTATIVA:');
      console.log('📧 Email digitado:', `"${formData.email}"`);
      console.log('🔐 Password length:', formData.password?.length || 0);
      console.log('🌐 URL completa:', `${API}/auth/login`);
      const requestData = { email: formData.email.trim(), password: formData.password };
      console.log('📦 Dados enviados:', JSON.stringify(requestData, null, 2));
      const response = await axios.post(`${API}/auth/login`, requestData);
      console.log('✅ STATUS RESPOSTA:', response.status);
      console.log('🏆 LOGIN SUCESSO! Redirecionando...');
      toast.success('Bem-vindo ao NexoSynX!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      console.log('💥 ERRO CAPTURADO:');
      console.log('🔍 Tipo:', error.constructor.name);
      console.log('📊 Status HTTP:', error.response?.status);
      console.log('📄 Resposta erro:', error.response?.data);
      console.log('🔗 URL tentativa:', error.config?.url);
      console.log('📝 Dados enviados:', error.config?.data);
      const message = error.response?.data?.detail || 'Erro ao realizar login';
      console.log('🗨️ Mensagem mostrada:', message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-hidden" style={{ background: '#0B1220' }}>
      {/* Animated network background */}
      <div className="absolute inset-0">
        <NetworkCanvas />
        {/* Radial vignette overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 30% 50%, rgba(37,99,235,0.08) 0%, transparent 70%), radial-gradient(ellipse at 70% 50%, rgba(0,212,203,0.06) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* LEFT PANEL — branding */}
      <div
        className={`hidden lg:flex flex-col justify-between flex-1 px-16 py-12 relative z-10 transition-all duration-1000 ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
      >
        <NexoSynXLogo size={52} showText={true} light={true} />

        <div className="max-w-lg">
          <div className="mb-6">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-6"
              style={{ background: 'rgba(0,212,203,0.12)', color: '#00D4CB', border: '1px solid rgba(0,212,203,0.25)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              Plataforma de crescimento
            </div>
            <h1
              className="text-5xl font-bold leading-tight mb-4"
              style={{ color: '#FFFFFF', letterSpacing: '-0.03em', fontFamily: "'Figtree', sans-serif" }}
            >
              Relacionamentos<br />
              <span style={{ background: 'linear-gradient(90deg, #2563EB, #00D4CB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                que geram resultados.
              </span>
            </h1>
            <p className="text-lg" style={{ color: 'rgba(229,231,235,0.65)', lineHeight: 1.7 }}>
              CRM completo com prospecção, automações e inteligência artificial integrada para acelerar suas vendas.
            </p>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3 mt-8">
            {[
              { icon: '👥', label: 'Gestão de Leads' },
              { icon: '🔗', label: 'Integrações' },
              { icon: '📊', label: 'Relatórios IA' },
              { icon: '🚀', label: 'Prospecção LinkedIn' },
              { icon: '⚡', label: 'Automações' },
            ].map(f => (
              <div
                key={f.label}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{ background: 'rgba(20,35,61,0.8)', color: '#E5E7EB', border: '1px solid rgba(37,99,235,0.2)' }}
              >
                <span>{f.icon}</span>
                {f.label}
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs" style={{ color: 'rgba(229,231,235,0.3)' }}>
          © 2026 NexoSynX · CRM Completo · Todos os direitos reservados
        </p>
      </div>

      {/* RIGHT PANEL — login form */}
      <div
        className={`flex items-center justify-center w-full lg:w-[480px] lg:min-w-[480px] relative z-10 px-6 py-12 transition-all duration-1000 ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
        style={{ background: 'rgba(11,18,32,0.75)', backdropFilter: 'blur(24px)', borderLeft: '1px solid rgba(37,99,235,0.12)' }}
      >
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <NexoSynXLogo size={44} showText={true} light={true} />
          </div>

          {/* Greeting */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
              Bem-vindo 👋
            </h2>
            <p className="text-sm" style={{ color: 'rgba(229,231,235,0.5)' }}>
              Acesse sua conta para continuar
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold tracking-wide uppercase" style={{ color: 'rgba(229,231,235,0.6)' }}>
                Email corporativo
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(229,231,235,0.35)' }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </span>
                <input
                  type="email"
                  data-testid="email-input"
                  placeholder="seu.email@empresa.com"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full h-12 pl-10 pr-4 rounded-xl text-sm font-medium outline-none transition-all"
                  style={{
                    background: 'rgba(20,35,61,0.6)',
                    border: '1px solid rgba(37,99,235,0.2)',
                    color: '#FFFFFF',
                    caretColor: '#00D4CB',
                  }}
                  onFocus={e => { e.target.style.border = '1px solid rgba(0,212,203,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,212,203,0.08)'; }}
                  onBlur={e => { e.target.style.border = '1px solid rgba(37,99,235,0.2)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold tracking-wide uppercase" style={{ color: 'rgba(229,231,235,0.6)' }}>
                Senha
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(229,231,235,0.35)' }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </span>
                <input
                  type={showPass ? 'text' : 'password'}
                  data-testid="password-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="w-full h-12 pl-10 pr-12 rounded-xl text-sm font-medium outline-none transition-all"
                  style={{
                    background: 'rgba(20,35,61,0.6)',
                    border: '1px solid rgba(37,99,235,0.2)',
                    color: '#FFFFFF',
                    caretColor: '#00D4CB',
                  }}
                  onFocus={e => { e.target.style.border = '1px solid rgba(0,212,203,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,212,203,0.08)'; }}
                  onBlur={e => { e.target.style.border = '1px solid rgba(37,99,235,0.2)'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'rgba(229,231,235,0.35)' }}
                  onMouseEnter={e => e.target.style.color = '#00D4CB'}
                  onMouseLeave={e => e.target.style.color = 'rgba(229,231,235,0.35)'}
                >
                  {showPass ? (
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full h-12 rounded-xl font-semibold text-sm transition-all duration-200 relative overflow-hidden group mt-2"
              style={{
                background: loading ? 'rgba(37,99,235,0.5)' : 'linear-gradient(135deg, #2563EB 0%, #00D4CB 100%)',
                color: '#FFFFFF',
                boxShadow: loading ? 'none' : '0 4px 24px rgba(37,99,235,0.35)',
              }}
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #00b8b0 100%)' }}
              />
              <span className="relative flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Entrando…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Entrar no Sistema
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(37,99,235,0.12)' }}>
            <div className="flex items-center justify-center gap-2 mb-3">
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#00D4CB' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-xs font-medium" style={{ color: 'rgba(229,231,235,0.4)' }}>
                Conexão segura e criptografada
              </span>
            </div>
            <p className="text-center text-xs" style={{ color: 'rgba(229,231,235,0.25)' }}>
              Problemas de acesso? Contate o administrador
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
